<template>
    <v-container fluid>
        <v-row>
            <v-col v-if="seafarer.id != 0">
                <v-btn v-if="inEditMode" class="ml-2 mr-2" color="primary" @click="saveClick(null)">
                    <v-icon small>fa-save</v-icon>&nbsp;{{ $t('buttons.save') }}
                </v-btn>
                <v-btn v-if="inEditMode" class="ml-2 mr-2" color="primary" @click="saveClick(true)">
                    <v-icon small>fa-save</v-icon>&nbsp;{{ $t('buttons.saveAndExit') }}
                </v-btn>
                <v-btn v-if="inEditMode" class="ml-2 mr-2" @click="cancelClick()">
                    <v-icon small>fa-times</v-icon>&nbsp;{{ $t('buttons.cancel') }}
                </v-btn>
                <v-btn
                    v-if="!inEditMode && !seafarer.gdpr_edit_lock"
                    class="ml-2 mr-2"
                    color="primary"
                    @click="editClick()"
                >
                    <v-icon small>fa-edit</v-icon>&nbsp;{{ $t('buttons.edit') }}
                </v-btn>
                <v-btn class="mr-2" @click="backClick">
                    <v-icon small>fa-reply</v-icon>&nbsp;{{ $t('buttons.back') }}
                </v-btn>
            </v-col>
        </v-row>
        <v-row
            style="
                margin-top: 3px;
                margin-bottom: 0px;
                scroll-margin: 0px;
                height: 75vh;
                overflow-y: scroll;
                overflow-x: hidden;
            "
        >
            <v-col cols="12" md="6">
                <v-card flat>
                    <v-card-title>
                        {{ $t('labels.education') }}
                    </v-card-title>
                    <v-card-text>
                        <v-simple-table>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="text-left">{{ $t('fields.schoolName') }}</th>
                                        <th class="text-left">{{ $t('fields.from') }}</th>
                                        <th class="text-left">{{ $t('fields.to') }}</th>
                                        <th class="text-left" v-if="inEditMode">{{ $t('buttons.delete') }}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in educationItems" :key="index">
                                        <td>{{ item.school_name }}</td>
                                        <td>{{ item.date_from_display }}</td>
                                        <td>{{ item.date_to_display }}</td>
                                        <td class="pl-1">
                                            <v-btn
                                                v-if="inEditMode"
                                                class="ml-2 mr-2"
                                                color="secondary"
                                                @click="removeEducation(index)"
                                            >
                                                <v-icon small>fa-trash</v-icon>
                                            </v-btn>
                                        </td>
                                    </tr>
                                    <tr v-if="!inEditMode && educationItems.length == 0">
                                        <td colspan="3" class="text--secondary">{{ $t('labels.noData') }}</td>
                                    </tr>
                                    <tr v-if="inEditMode">
                                        <td class="pl-1">
                                            <InputField
                                                :maxlength="200"
                                                v-model="educationObject.school_name"
                                                :errorBorder="!educationObjectValidation.school_name"
                                                :label="'* ' + $t('fields.schoolName')"
                                                @keyup.enter.native="addEducation()"
                                            ></InputField>
                                        </td>
                                        <td class="pl-1">
                                            <DatePicker
                                                v-model="educationObject.date_from"
                                                :errorBorder="!educationObjectValidation.date_from"
                                                :date-label="'* ' + $t('fields.from')"
                                            ></DatePicker>
                                        </td>
                                        <td class="pl-1">
                                            <DatePicker
                                                v-model="educationObject.date_to"
                                                :errorBorder="!educationObjectValidation.date_to"
                                                :date-label="'* ' + $t('fields.to')"
                                            ></DatePicker>
                                        </td>
                                        <td class="pl-1">
                                            <v-btn class="ml-2 mr-2 mt-1" color="primary" @click="addEducation()">
                                                <v-icon small>fa-plus</v-icon>
                                            </v-btn>
                                        </td>
                                    </tr>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card-text>
                </v-card>
            </v-col>

            <v-col cols="12" md="6" class="pa-0 ma-0">
                <SeafarerLanguages
                    ref="languages"
                    :inEditMode="inEditMode"
                    :seafarer="seafarer"
                    :languagesItems="languagesItems"
                    @pending="languagePending = $event"
                ></SeafarerLanguages>
            </v-col>

            <v-col cols="12" md="6">
                <v-row>
                    <v-card flat>
                        <v-card-title>
                            {{ $t('labels.bodyMetrics') }}
                        </v-card-title>
                        <v-card-text>
                            <v-row dense wrap>
                                <v-col cols="12">
                                    <InputField
                                        :readonly="!inEditMode"
                                        :maxlength="5"
                                        v-model="seafarerBodyMetrics.height"
                                        :errorBorder="!seafarerBodyMetricsValidation.height"
                                        :label="$t('fields.heightCm')"
                                    ></InputField>
                                </v-col>
                                <v-col cols="12">
                                    <InputField
                                        :readonly="!inEditMode"
                                        :maxlength="3"
                                        v-model="seafarerBodyMetrics.weight"
                                        :errorBorder="!seafarerBodyMetricsValidation.weight"
                                        :label="$t('fields.weightKg')"
                                    ></InputField>
                                </v-col>
                                <v-col cols="12">
                                    <InputField
                                        :readonly="!inEditMode"
                                        :maxlength="2"
                                        v-model="seafarerBodyMetrics.shoe_size_eur"
                                        :errorBorder="!seafarerBodyMetricsValidation.shoe_size_eur"
                                        :label="$t('fields.shoeSizeEur')"
                                    ></InputField>
                                </v-col>
                                <v-col cols="12">
                                    <Dropdown
                                        :readonly="!inEditMode"
                                        v-model="seafarerBodyMetrics.overall_size_id"
                                        :ajax="true"
                                        :data="'overall-sizes'"
                                        :errorBorder="!seafarerBodyMetricsValidation.overall_size_id"
                                        :label="$t('fields.overallSize')"
                                    ></Dropdown>
                                </v-col>
                            </v-row>
                        </v-card-text>
                    </v-card>
                </v-row>
            </v-col>
            <v-col cols="12" md="6" class="pa-0 ma-0">
                <SeafarerExperienceInRank :reload="reload" :seafarer="seafarer" />
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import InputField from './trinkets/InputField';
import Dropdown from './trinkets/Dropdown';
import DatePicker from '@/components/trinkets/DatePicker';
import Education from '../models/Education';
import SeafarerBodyMetrics from '../models/SeafarerBodyMetrics';
import SeafarerLanguages from './SeafarerLanguages';
import SeafarerExperienceInRank from './SeafarerExperienceInRank';

// Kolone su string u bazi; dopuštamo broj s opcionalnim decimalama (točka ili zarez).
const BODY_METRIC_PATTERN = /^\d{1,3}([.,]\d{1,2})?$/;
const BODY_METRIC_FIELDS = ['height', 'weight', 'shoe_size_eur'];

export default {
    name: 'SeafarerAdditional',
    components: {DatePicker, InputField, SeafarerLanguages, SeafarerExperienceInRank, Dropdown},
    data: function () {
        return {
            educationObject: new Education({}),
            educationObjectValidation: new Education({}, true),
            seafarerBodyMetrics: new SeafarerBodyMetrics({}),
            seafarerBodyMetricsValidation: new SeafarerBodyMetrics({}, true),
            initialLoad: false,
            languagesItems: [],
            languagePending: false,
            lockKey: 'additional',
            lockTimer: null,
            educationItems: [],
            // Stanje sekcija u trenutku ulaska u edit mode — za dirty tracking i spremanje samo promijenjenog.
            snapshot: {education: '', languages: '', bodyMetrics: ''},
        };
    },
    props: ['seafarer', 'inEditMode', 'reload'],
    emits: ['edit', 'dirty'],
    computed: {
        isDirty() {
            if (!this.inEditMode) return false;
            return this.changedSections().length > 0 || !this.isEducationRowEmpty() || this.languagePending;
        },
    },
    watch: {
        inEditMode(value) {
            if (value) {
                this.takeSnapshot();
            } else {
                // Izlaz iz edit moda (save, odustani ili napuštanje taba) — uvijek prikaži stanje iz baze,
                // da nespremljene izmjene ne ostanu na ekranu kao da su spremljene.
                this.resetPendingRows();
                this.loadData();
            }
        },
        isDirty(value) {
            this.$emit('dirty', value);
        },
    },
    methods: {
        isEducationRowEmpty() {
            const o = this.educationObject;
            return (o.school_name == '' || o.school_name == null) && o.date_from == null && o.date_to == null;
        },

        addEducation() {
            let success = true;

            if (this.educationObject.school_name == '' || this.educationObject.school_name == null) {
                success = false;
                this.educationObjectValidation.school_name = false;
            } else {
                this.educationObjectValidation.school_name = true;
            }
            if (this.educationObject.date_from == null) {
                success = false;
                this.educationObjectValidation.date_from = false;
            } else {
                this.educationObjectValidation.date_from = true;
            }
            if (this.educationObject.date_to == null) {
                success = false;
                this.educationObjectValidation.date_to = false;
            } else {
                this.educationObjectValidation.date_to = true;
            }
            if (this.educationObject.date_from > this.educationObject.date_to) {
                success = false;
                this.educationObjectValidation.date_from = false;
                this.educationObjectValidation.date_to = false;
            }

            if (success) {
                this.educationObject.date_from_display = this.displayDate(this.educationObject.date_from);
                this.educationObject.date_to_display = this.displayDate(this.educationObject.date_to);

                this.educationItems.push(this.educationObject);
                this.educationObject = new Education({});
            }
            return success;
        },

        // Isti format kao backend (SeafarerEducationController@getAll: 'j.n.Y.').
        displayDate(value) {
            const [year, month, day] = value.split('-');
            return parseInt(day) + '.' + parseInt(month) + '.' + year + '.';
        },

        validateBodyMetrics() {
            let success = true;
            BODY_METRIC_FIELDS.forEach((field) => {
                const value = this.seafarerBodyMetrics[field];
                const valid = value === null || value === '' || BODY_METRIC_PATTERN.test(String(value));
                this.seafarerBodyMetricsValidation[field] = valid;
                if (!valid) success = false;
            });
            return success;
        },

        serializeSection(section) {
            if (section === 'education') return JSON.stringify(this.educationItems);
            if (section === 'languages') return JSON.stringify(this.languagesItems);
            return JSON.stringify(this.seafarerBodyMetrics);
        },

        takeSnapshot() {
            this.snapshot = {
                education: this.serializeSection('education'),
                languages: this.serializeSection('languages'),
                bodyMetrics: this.serializeSection('bodyMetrics'),
            };
        },

        changedSections() {
            return Object.keys(this.snapshot).filter(
                (section) => this.serializeSection(section) !== this.snapshot[section],
            );
        },

        resetPendingRows() {
            this.educationObject = new Education({});
            this.educationObjectValidation = new Education({}, true);
            this.seafarerBodyMetricsValidation = new SeafarerBodyMetrics({}, true);
            if (this.$refs.languages) this.$refs.languages.resetPending();
        },

        editClick() {
            this.$emit('edit');
        },
        cancelClick() {
            // Watcher na inEditMode vraća podatke iz baze.
            this.$emit('edit');
        },
        removeEducation(index) {
            this.educationItems.splice(index, 1);
        },

        backClick() {
            this.$router.go(-1);
        },
        saveClick(goBack) {
            const vm = this;

            // Ispunjen, a ne dodan red (bez klika na "+") preuzmi automatski; djelomično ispunjen blokira spremanje.
            if (!this.isEducationRowEmpty() && !this.addEducation()) {
                this.$alert(this.$t('alerts.pendingRowInvalid'));
                return;
            }
            if (!this.$refs.languages.commitPending()) {
                return;
            }

            const changed = this.changedSections();
            if (changed.includes('bodyMetrics') && !this.validateBodyMetrics()) {
                this.$alert(this.$t('alerts.invalidBodyMetrics'));
                return;
            }

            const savers = {
                education: {label: this.$t('labels.education'), save: this.saveEducation},
                languages: {label: this.$t('labels.languages'), save: this.saveLanguages},
                bodyMetrics: {label: this.$t('labels.bodyMetrics'), save: this.saveBodyMetrics},
            };

            const finish = () => {
                vm.$emit('edit');
                vm.$triggerReload();
                vm.$notify(vm.$t('alerts.basicUpdate'));
                if (goBack) vm.backClick();
            };

            if (changed.length === 0) {
                finish();
                return;
            }

            let pending = changed.length;
            const failed = [];
            // Uspjeh i izlaz iz edit moda tek kad sva spremanja stvarno završe.
            changed.forEach((section) => {
                const sent = this.serializeSection(section);
                savers[section].save((ok) => {
                    if (ok) {
                        // Uspješno spremljena sekcija se ne šalje ponovno ako neka druga padne.
                        vm.snapshot[section] = sent;
                    } else {
                        failed.push(savers[section].label);
                    }
                    if (--pending > 0) return;
                    if (failed.length > 0) {
                        vm.$alert(vm.$t('alerts.sectionsNotSaved').split('%sections%').join(failed.join(', ')));
                        return;
                    }
                    finish();
                });
            });
        },

        saveEducation(done) {
            let vm = this;
            let addition = '/' + vm.seafarer.id;
            this.$APICall('post', 'seafarer-educations' + addition, this.educationItems, (data, success) => {
                if (!success) {
                    // Osvježi lokalnu listu iz baze (delete-then-recreate mogao je djelomično proći).
                    vm.$APICall('get', 'seafarer-educations/' + vm.seafarer.id, {}, (data, ok) => {
                        if (ok) vm.educationItems = data.items;
                    });
                }
                if (done) done(success);
            });
        },

        saveLanguages(done) {
            let vm = this;
            let addition = '/' + vm.seafarer.id;
            this.$APICall('post', 'seafarer-languages' + addition, this.languagesItems, (data, success) => {
                if (done) done(success);
            });
        },

        saveBodyMetrics(done) {
            let vm = this;
            let addition = '/' + vm.seafarer.id;
            this.$APICall('put', 'seafarer-body-metrics' + addition, this.seafarerBodyMetrics, (data, success) => {
                if (done) done(success);
            });
        },

        loadData() {
            if (this.seafarer.id == 0) return;
            let vm = this;
            this.$APICall('get', 'seafarer-educations/' + this.seafarer.id, {}, (data, success) => {
                if (success) {
                    vm.educationItems = data.items;
                }
            });
            this.$APICall('get', 'seafarer-languages/' + this.seafarer.id, {}, (data, success) => {
                if (success) {
                    vm.languagesItems = data.items;
                }
            });
            this.$APICall('get', 'seafarer-body-metrics/' + this.seafarer.id, {}, (data, success) => {
                if (success) {
                    vm.seafarerBodyMetrics = data;
                }
            });
        },
    },
    mounted() {
        if (!this.$myStore.state.loggedIn) {
            this.$router.replace('login');
        }
        this.loadData();
    },
};
</script>

<style scoped></style>
